import React, { useEffect, useState, useCallback } from 'react';
import { Wrench, Plus, FileText, Upload, Trash2, Download, Search, Filter, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api, { propertiesAPI, clientsAPI } from '../lib/api';
import { toast } from 'sonner';
import ModuleTasksPanel from '../components/ModuleTasksPanel';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const REPAIR_TYPES = [
  { value: 'plomberie', label: 'Plomberie' },
  { value: 'electricite', label: 'Electricite' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'climatisation', label: 'Climatisation' },
  { value: 'menuiserie', label: 'Menuiserie' },
  { value: 'maconnerie', label: 'Maconnerie' },
  { value: 'serrurerie', label: 'Serrurerie' },
  { value: 'nettoyage', label: 'Nettoyage' },
  { value: 'autre', label: 'Autre' },
];

const STATUS_LABELS = {
  planifiee: { label: 'Planifiee', cls: 'bg-blue-100 text-blue-800' },
  en_cours: { label: 'En cours', cls: 'bg-amber-100 text-amber-800' },
  terminee: { label: 'Terminee', cls: 'bg-emerald-100 text-emerald-800' },
  annulee: { label: 'Annulee', cls: 'bg-gray-100 text-gray-700' },
};

const PAID_BY_LABELS = {
  owner: { label: 'Proprietaire', cls: 'bg-purple-100 text-purple-800' },
  agency: { label: 'Agence', cls: 'bg-teal-100 text-teal-800' },
  tenant: { label: 'Locataire', cls: 'bg-orange-100 text-orange-800' },
};

const EMPTY_FORM = {
  property_id: '',
  unit_id: '',
  tenant_id: '',
  repair_type: 'plomberie',
  description: '',
  provider_name: '',
  provider_phone: '',
  scheduled_date: '',
  completion_date: '',
  labor_cost: 0,
  materials_cost: 0,
  other_cost: 0,
  paid_by: 'owner',
  status: 'planifiee',
  notes: '',
};

const fmtMoney = (n) => `${(Number(n) || 0).toLocaleString('fr-FR')} FCFA`;
const fmtDate = (s) => s ? format(new Date(s.length === 10 ? `${s}T00:00:00` : s), 'dd MMM yyyy', { locale: fr }) : '-';

const Repairs = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [detailRepair, setDetailRepair] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p, c] = await Promise.all([
        api.get('/repairs'),
        propertiesAPI.getAll(),
        clientsAPI.getAll(),
      ]);
      setList(r.data || []);
      setProperties(p.data || []);
      setClients(c.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Pre-fill from sessionStorage if coming from a tenant request
  useEffect(() => {
    const raw = sessionStorage.getItem('repair_prefill');
    if (raw) {
      try {
        const data = JSON.parse(raw);
        setEditing(null);
        setForm({ ...EMPTY_FORM, ...data });
        setFormOpen(true);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('repair_prefill parse failed', err);
      }
      sessionStorage.removeItem('repair_prefill');
    }
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      property_id: r.property_id || '',
      unit_id: r.unit_id || '',
      tenant_id: r.tenant_id || '',
      repair_type: r.repair_type || 'plomberie',
      description: r.description || '',
      provider_name: r.provider_name || '',
      provider_phone: r.provider_phone || '',
      scheduled_date: r.scheduled_date || '',
      completion_date: r.completion_date || '',
      labor_cost: r.labor_cost || 0,
      materials_cost: r.materials_cost || 0,
      other_cost: r.other_cost || 0,
      paid_by: r.paid_by || 'owner',
      status: r.status || 'planifiee',
      notes: r.notes || '',
    });
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.property_id) {
      toast.error('Selectionnez un bien');
      return;
    }
    if (!form.description?.trim()) {
      toast.error('Saisissez une description');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        labor_cost: parseFloat(form.labor_cost) || 0,
        materials_cost: parseFloat(form.materials_cost) || 0,
        other_cost: parseFloat(form.other_cost) || 0,
        unit_id: form.unit_id || null,
        tenant_id: form.tenant_id || null,
        scheduled_date: form.scheduled_date || null,
        completion_date: form.completion_date || null,
        source_request_id: form.source_request_id || null,
      };
      if (editing) {
        await api.put(`/repairs/${editing.id}`, payload);
        toast.success('Reparation mise a jour');
      } else {
        await api.post('/repairs', payload);
        toast.success('Reparation creee');
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Supprimer la reparation ${r.reference} ?`)) return;
    try {
      await api.delete(`/repairs/${r.id}`);
      toast.success('Supprimee');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownloadPdf = async (r) => {
    try {
      const res = await api.get(`/repairs/${r.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${r.reference}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Impossible de telecharger le PDF');
    }
  };

  const handleUploadInvoice = async (repairId, file, label = '') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('label', label);
    try {
      await api.post(`/repairs/${repairId}/invoices`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Facture jointe');
      // Refresh detail
      const r = await api.get(`/repairs/${repairId}`);
      setDetailRepair(r.data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    }
  };

  const handleDeleteInvoice = async (repairId, invoiceId) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await api.delete(`/repairs/${repairId}/invoices/${invoiceId}`);
      toast.success('Facture supprimee');
      const r = await api.get(`/repairs/${repairId}`);
      setDetailRepair(r.data);
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const filtered = list.filter((r) => {
    const txt = search.toLowerCase();
    const matchSearch = !txt || (r.reference?.toLowerCase().includes(txt)
      || r.description?.toLowerCase().includes(txt)
      || r.property_title?.toLowerCase().includes(txt)
      || r.tenant_name?.toLowerCase().includes(txt)
      || r.provider_name?.toLowerCase().includes(txt));
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totals = {
    count: list.length,
    cost: list.reduce((acc, r) => acc + (r.total_cost || 0), 0),
    inProgress: list.filter(r => r.status === 'en_cours').length,
    done: list.filter(r => r.status === 'terminee').length,
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 animate-fade-up" data-testid="repairs-page">
      <ModuleTasksPanel moduleKey="repairs" moduleLabel="Reparations" />
      <div className="page-header flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1B3A4B] flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-gold-gradient flex items-center justify-center shadow-md">
              <Wrench className="w-5 h-5 text-[#1B3A4B]" />
            </span>
            Reparations
          </h1>
          <p className="text-sm text-gray-500 mt-2">Suivi des interventions et factures associees</p>
        </div>
        <Button onClick={openCreate} className="btn-brand rounded-xl px-4 py-2.5 h-auto shadow-md" data-testid="new-repair-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle reparation
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="kpi-card p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-[#1B3A4B]/60 uppercase tracking-wider font-semibold">Total</p>
            <span className="w-8 h-8 rounded-lg bg-[#1B3A4B]/10 flex items-center justify-center">
              <Wrench className="w-4 h-4 text-[#1B3A4B]" />
            </span>
          </div>
          <p className="text-3xl font-bold text-[#1B3A4B]">{totals.count}</p>
        </div>
        <div className="kpi-card p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-amber-700/70 uppercase tracking-wider font-semibold">En cours</p>
            <span className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            </span>
          </div>
          <p className="text-3xl font-bold text-amber-600">{totals.inProgress}</p>
        </div>
        <div className="kpi-card p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-emerald-700/70 uppercase tracking-wider font-semibold">Terminees</p>
            <span className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
            </span>
          </div>
          <p className="text-3xl font-bold text-emerald-600">{totals.done}</p>
        </div>
        <div className="kpi-card p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-[#D4A745] uppercase tracking-wider font-semibold">Cout total</p>
            <span className="w-8 h-8 rounded-lg bg-gold-gradient-soft flex items-center justify-center">
              <span className="text-[#1B3A4B] font-bold text-xs">FCFA</span>
            </span>
          </div>
          <p className="text-xl font-bold text-[#1B3A4B]">{fmtMoney(totals.cost)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (reference, bien, locataire, prestataire)..."
            className="pl-9" data-testid="repair-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="repair-status-filter">
            <Filter className="w-4 h-4 mr-2" /> <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_LABELS).map(([v, c]) => <SelectItem key={v} value={v}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-gray-500 text-center py-8">Chargement...</p>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500">
          <Wrench className="w-10 h-10 mx-auto opacity-30 mb-3" />
          <p>Aucune reparation pour l'instant</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const st = STATUS_LABELS[r.status] || STATUS_LABELS.planifiee;
            const pb = PAID_BY_LABELS[r.paid_by] || PAID_BY_LABELS.owner;
            return (
              <Card key={r.id} className="hover:shadow transition cursor-pointer" data-testid={`repair-${r.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#1B3A4B] text-[#D4A745] flex items-center justify-center flex-shrink-0">
                      <Wrench className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0" onClick={() => setDetailRepair(r)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] font-mono">{r.reference}</Badge>
                        <Badge className={st.cls}>{st.label}</Badge>
                        <Badge className={pb.cls}>{pb.label}</Badge>
                        <span className="text-sm font-semibold text-gray-700 capitalize">{r.repair_type_label}</span>
                        {r.invoices?.length > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <FileText className="w-3 h-3 mr-1" /> {r.invoices.length} facture(s)
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 mt-1 line-clamp-2">{r.description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500 mt-2 flex-wrap">
                        {r.property_title && <span>Bien: <span className="font-medium text-gray-700">{r.property_title}</span></span>}
                        {r.tenant_name && <span>Locataire: <span className="font-medium text-gray-700">{r.tenant_name}</span></span>}
                        {r.provider_name && <span>Prestataire: <span className="font-medium text-gray-700">{r.provider_name}</span></span>}
                        <span>Cree le {fmtDate(r.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <p className="text-lg font-bold text-[#1B3A4B]">{fmtMoney(r.total_cost)}</p>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setDetailRepair(r)} data-testid={`open-${r.id}`}>
                          Detail
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadPdf(r)} title="PDF">
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} title="Modifier">
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(r)} title="Supprimer" className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Form modal */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier la reparation' : 'Nouvelle reparation'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Bien concerne *</Label>
                <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                  <SelectTrigger data-testid="repair-property"><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title || p.address}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Locataire (optionnel)</Label>
                <Select value={form.tenant_id || 'none'} onValueChange={(v) => setForm({ ...form, tenant_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {clients.filter(c => c.client_type === 'locataire').map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Type *</Label>
                <Select value={form.repair_type} onValueChange={(v) => setForm({ ...form, repair_type: v })}>
                  <SelectTrigger data-testid="repair-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REPAIR_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([v, c]) => <SelectItem key={v} value={v}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description du probleme *</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Decrire en detail le probleme..." rows={3} data-testid="repair-description" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Prestataire / Artisan</Label>
                <Input value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })}
                  placeholder="Nom" />
              </div>
              <div>
                <Label>Telephone prestataire</Label>
                <Input value={form.provider_phone} onChange={(e) => setForm({ ...form, provider_phone: e.target.value })}
                  placeholder="+223 ..." />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Date prevue</Label>
                <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} />
              </div>
              <div>
                <Label>Date realisee</Label>
                <Input type="date" value={form.completion_date} onChange={(e) => setForm({ ...form, completion_date: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Main d'oeuvre (FCFA)</Label>
                <Input type="number" min="0" value={form.labor_cost} onChange={(e) => setForm({ ...form, labor_cost: e.target.value })} data-testid="repair-labor" />
              </div>
              <div>
                <Label>Materiaux (FCFA)</Label>
                <Input type="number" min="0" value={form.materials_cost} onChange={(e) => setForm({ ...form, materials_cost: e.target.value })} />
              </div>
              <div>
                <Label>Autres (FCFA)</Label>
                <Input type="number" min="0" value={form.other_cost} onChange={(e) => setForm({ ...form, other_cost: e.target.value })} />
              </div>
            </div>

            <div className="bg-[#1B3A4B]/5 rounded p-3 text-sm">
              <span className="text-gray-600">Total :</span>
              <span className="ml-2 font-bold text-[#1B3A4B]">
                {fmtMoney((parseFloat(form.labor_cost) || 0) + (parseFloat(form.materials_cost) || 0) + (parseFloat(form.other_cost) || 0))}
              </span>
            </div>

            <div>
              <Label>Pris en charge par</Label>
              <Select value={form.paid_by} onValueChange={(v) => setForm({ ...form, paid_by: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PAID_BY_LABELS).map(([v, c]) => <SelectItem key={v} value={v}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="repair-save-btn">
                {saving ? 'Enregistrement...' : (editing ? 'Mettre a jour' : 'Creer')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail modal with invoices */}
      <Dialog open={!!detailRepair} onOpenChange={(o) => !o && setDetailRepair(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailRepair && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-[#D4A745]" />
                  {detailRepair.reference}
                  <Badge className={STATUS_LABELS[detailRepair.status]?.cls}>{STATUS_LABELS[detailRepair.status]?.label}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-gray-500">Type</p><p className="font-semibold capitalize">{detailRepair.repair_type_label || detailRepair.repair_type}</p></div>
                  <div><p className="text-xs text-gray-500">Pris en charge par</p><p className="font-semibold">{PAID_BY_LABELS[detailRepair.paid_by]?.label}</p></div>
                  <div><p className="text-xs text-gray-500">Bien</p><p className="font-semibold">{detailRepair.property_title || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Locataire</p><p className="font-semibold">{detailRepair.tenant_name || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Date prevue</p><p className="font-semibold">{fmtDate(detailRepair.scheduled_date)}</p></div>
                  <div><p className="text-xs text-gray-500">Date realisee</p><p className="font-semibold">{fmtDate(detailRepair.completion_date)}</p></div>
                  <div><p className="text-xs text-gray-500">Prestataire</p><p className="font-semibold">{detailRepair.provider_name || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Telephone</p><p className="font-semibold">{detailRepair.provider_phone || '-'}</p></div>
                </div>

                <div className="bg-gray-50 rounded p-3 text-sm">
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p>{detailRepair.description}</p>
                </div>

                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="bg-gray-50 rounded p-2"><p className="text-[10px] text-gray-500">M.O</p><p className="font-bold">{fmtMoney(detailRepair.labor_cost)}</p></div>
                  <div className="bg-gray-50 rounded p-2"><p className="text-[10px] text-gray-500">Materiaux</p><p className="font-bold">{fmtMoney(detailRepair.materials_cost)}</p></div>
                  <div className="bg-gray-50 rounded p-2"><p className="text-[10px] text-gray-500">Autres</p><p className="font-bold">{fmtMoney(detailRepair.other_cost)}</p></div>
                  <div className="bg-[#D4A745]/20 rounded p-2"><p className="text-[10px] text-gray-700">TOTAL</p><p className="font-bold text-[#1B3A4B]">{fmtMoney(detailRepair.total_cost)}</p></div>
                </div>

                {/* Invoices */}
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Factures jointes
                    </h4>
                    <label className="cursor-pointer">
                      <input type="file" accept="application/pdf,image/*" className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUploadInvoice(detailRepair.id, f, f.name);
                          e.target.value = '';
                        }}
                        data-testid={`invoice-upload-${detailRepair.id}`}
                      />
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#1B3A4B] text-white text-xs hover:bg-[#142D3A]">
                        <Upload className="w-3 h-3" /> Joindre
                      </span>
                    </label>
                  </div>

                  {(!detailRepair.invoices || detailRepair.invoices.length === 0) ? (
                    <p className="text-xs text-gray-400 text-center py-3">Aucune facture jointe</p>
                  ) : (
                    <div className="space-y-2">
                      {detailRepair.invoices.map((inv) => (
                        <div key={inv.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded p-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{inv.label || inv.file_name}</p>
                              <p className="text-[10px] text-gray-500">Par {inv.uploaded_by} le {fmtDate(inv.uploaded_at)}</p>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${inv.file_url}`} target="_blank" rel="noreferrer"
                              className="px-2 py-1 rounded border text-xs hover:bg-white">Ouvrir</a>
                            <Button size="sm" variant="ghost" className="text-red-600 h-7 px-2"
                              onClick={() => handleDeleteInvoice(detailRepair.id, inv.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {detailRepair.notes && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
                    <p className="text-xs text-amber-700 mb-1">Notes</p>
                    <p>{detailRepair.notes}</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => handleDownloadPdf(detailRepair)}>
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                <Button onClick={() => { setFormOpen(true); setDetailRepair(null); openEdit(detailRepair); }}
                  className="bg-[#1B3A4B] hover:bg-[#142D3A]">
                  Modifier
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Repairs;
